import { type MutableRefObject, useCallback, useRef, useState } from 'react';

import type { WinchMqttUser } from '../winchMqttApi';
import { buildVerticalScanStatusCommand, winchMqttTopics } from '../winchMqttApi';

import type { WinchMqttPublish } from './handleWinchMqttMessage';

const SCAN_START_SETTLE_MS = 5_000;

type ScanStartTrackingInput = {
    winchId: number | null;
    userRef: MutableRefObject<WinchMqttUser>;
    publish: WinchMqttPublish;
};

export type ScanStartTracking = {
    /** Start was sent and the device has not yet answered it or a later scan status request. */
    scanStarting: boolean;
    /** Sends a scan status request, noting it against a pending Start. */
    publishScanStatusRef: MutableRefObject<() => void>;
    onScanEnablePublished: (requestId: string) => void;
    onScanReply: (requestId: string | undefined, scanRunning: boolean) => void;
    resetScanStart: () => void;
};

/** Tracks a vertical scan Start until the device confirms it. */
export function useScanStartTracking(input: ScanStartTrackingInput): ScanStartTracking {
    const { winchId, userRef, publish } = input;

    // Start is confirmed by the reply to that enable request, or by a scan status reply.
    // The device may answer a status sent right after Start before it applies the enable,
    // so those replies count only when scanning is on. Later status polls count either way,
    // so a lost enable reply cannot block Move for good.
    const scanStartRef = useRef<{
        enableId: string;
        sentAt: number;
        earlyStatusIds: Set<string>;
        statusIds: Set<string>;
    } | null>(null);
    const [scanStarting, setScanStarting] = useState(false);
    const publishScanStatusRef = useRef<() => void>(() => undefined);
    publishScanStatusRef.current = () => {
        if (winchId == null) {
            return;
        }
        const command = buildVerticalScanStatusCommand(userRef.current);
        const start = scanStartRef.current;
        if (start) {
            const early = Date.now() - start.sentAt < SCAN_START_SETTLE_MS;
            (early ? start.earlyStatusIds : start.statusIds).add(command.uuid);
        }
        publish(winchMqttTopics(winchId).publish.verticalScanStatus, command.payload, {
            qos: 1,
        });
    };
    const onScanEnablePublished = useCallback((requestId: string): void => {
        scanStartRef.current = {
            enableId: requestId,
            sentAt: Date.now(),
            earlyStatusIds: new Set(),
            statusIds: new Set(),
        };
        setScanStarting(true);
        publishScanStatusRef.current();
    }, []);
    const onScanReply = (requestId: string | undefined, scanRunning: boolean): void => {
        const start = scanStartRef.current;
        if (start == null || requestId == null) {
            return;
        }
        const confirmed =
            requestId === start.enableId ||
            start.statusIds.has(requestId) ||
            (scanRunning && start.earlyStatusIds.has(requestId));
        if (!confirmed) {
            return;
        }
        scanStartRef.current = null;
        setScanStarting(false);
    };
    const resetScanStart = useCallback((): void => {
        scanStartRef.current = null;
        setScanStarting(false);
    }, []);

    return {
        scanStarting,
        publishScanStatusRef,
        onScanEnablePublished,
        onScanReply,
        resetScanStart,
    };
}
